import { Vector2, type Vector3 } from 'three';
import { vector3 } from '@/domain/geometry/vector3';
import type {
    LandmarkMap,
    RayAngleRefs,
    RayReference,
    CurveAngleRefs,
} from '@/domain/anatomy/hipSpineAngleOverlays';
import { HipSpineLandmarkName } from '@/domain/anatomy/hipSpineLandmarks';
import { horizontalUnit2, verticalUnit2 } from '@/domain/geometry/vector2';

// ---------------------------------------------------------------------------
// Geometry resolution — convert landmark refs to concrete positions
// ---------------------------------------------------------------------------

export function averagePositions(landmarkMap: LandmarkMap, names: string[]): Vector2 | null {
    const points: Vector2[] = [];
    for (const name of names) {
        const p = landmarkMap.get(name);
        if (!p) return null;
        points.push(p);
    }
    if (points.length === 0) return null;
    const sum = points.reduce((acc, p) => acc.add(p), new Vector2(0, 0));
    return sum.divideScalar(points.length);
}

export function toWorld(v: Vector2): Vector3 {
    return vector3(v.x, v.y, 0);
}

function anteriorDir(landmarkMap: LandmarkMap): Vector2 | null {
    const ant = landmarkMap.get(HipSpineLandmarkName.SacrumAnterior);
    const post = landmarkMap.get(HipSpineLandmarkName.SacrumPosterior);
    if (!ant || !post) return null;
    return ant.clone().sub(post);
}

export type RayGeometry = {
    vertex: Vector2;
    ray1End: Vector2;
    ray2End: Vector2;
    extraDots: Vector2[];
};

type Ray1Direction = {
    /** Unit/raw direction for ray 1 (caller normalises). */
    dir: Vector2;
    /** The resolved target point when ray 1 follows a landmark, else null (perpendicular ray). */
    target: Vector2 | null;
};

/**
 * Resolve ray 1's direction, either perpendicular to an endplate or pointing at
 * an averaged target landmark. Returns null when the required landmarks are
 * missing.
 */
function resolveRay1Direction(
    refs: RayAngleRefs,
    landmarkMap: LandmarkMap,
    ray1Start: Vector2,
): Ray1Direction | null {
    if (refs.ray1Landmarks.length < 2) return null;
    const epTarget = averagePositions(landmarkMap, refs.ray1Landmarks.slice(1));
    if (!epTarget) return null;

    if (refs.perpendicularRay1) {
        const endplateDir = epTarget.clone().sub(ray1Start);
        const perp1 = new Vector2(endplateDir.y, -endplateDir.x);
        const perp2 = new Vector2(-endplateDir.y, endplateDir.x);
        return { dir: perp1.y < perp2.y ? perp1 : perp2, target: null };
    }

    return { dir: epTarget.clone().sub(ray1Start), target: epTarget };
}

type Ray2Direction = {
    /** Unit direction for ray 2. */
    dir: Vector2;
    /** Ray 2's length in world units (falls back to `fallbackRayLength`). */
    length: number;
};

/**
 * Fixed reference-ray direction used when an angle has no ray-2 landmarks and is
 * instead measured against a vertical or horizontal reference line. The
 * horizontal reference (the default) is signed to share ray1's x-direction so
 * the arc sits on ray1's side regardless of which way the patient faces.
 */
function referenceDirection(reference: RayReference | undefined, ray1Dir: Vector2): Vector2 {
    if (reference === 'vertical') return verticalUnit2();
    return horizontalUnit2().multiplyScalar(ray1Dir.x >= 0 ? 1 : -1);
}

/**
 * Resolve ray 2's direction and length from its averaged landmark target.
 * Returns null when the target landmark is missing. The no-landmark case (a
 * fixed reference ray) is handled by the caller via {@link referenceDirection}.
 */
function resolveRay2Direction(
    refs: RayAngleRefs,
    landmarkMap: LandmarkMap,
    vertex: Vector2,
    fallbackRayLength: number,
): Ray2Direction | null {
    const ray2Target = averagePositions(landmarkMap, refs.ray2Landmarks);
    if (!ray2Target) return null;
    const ray2Vec = ray2Target.clone().sub(vertex);
    return { dir: ray2Vec.clone().normalize(), length: ray2Vec.length() || fallbackRayLength };
}

function collectExtraDots(refs: RayAngleRefs, landmarkMap: LandmarkMap): Vector2[] {
    const extraDots: Vector2[] = [];
    if (!refs.extraDotLandmarks) return extraDots;
    for (const name of refs.extraDotLandmarks) {
        const p = landmarkMap.get(name);
        if (p) extraDots.push(p);
    }
    return extraDots;
}

export function resolveRayGeometry(
    refs: RayAngleRefs,
    landmarkMap: LandmarkMap,
    scale: number,
    rayLengthFactor: number,
): RayGeometry | null {
    const vertex = averagePositions(landmarkMap, refs.vertexLandmarks);
    if (!vertex) return null;

    const ray1Start = landmarkMap.get(refs.ray1Landmarks[0]);
    if (!ray1Start) return null;

    const ray1 = resolveRay1Direction(refs, landmarkMap, ray1Start);
    if (!ray1) return null;
    ray1.dir.normalize();

    const fallbackRayLength = scale * rayLengthFactor;
    const ray1Length = ray1.target
        ? Math.max(
              ray1.target.clone().sub(vertex).dot(ray1.dir) || fallbackRayLength,
              fallbackRayLength,
          )
        : fallbackRayLength;
    const ray1End = vertex.clone().add(ray1.dir.clone().multiplyScalar(ray1Length));

    // With no ray-2 landmarks, ray 2 is the fixed reference line named by
    // refs.ray2Reference; otherwise it points at the averaged ray-2 landmark.
    const ray2 =
        refs.ray2Landmarks.length === 0
            ? { dir: referenceDirection(refs.ray2Reference, ray1.dir), length: fallbackRayLength }
            : resolveRay2Direction(refs, landmarkMap, vertex, fallbackRayLength);
    if (!ray2) return null;
    const ray2End = vertex.clone().add(ray2.dir.clone().multiplyScalar(ray2.length));

    return { vertex, ray1End, ray2End, extraDots: collectExtraDots(refs, landmarkMap) };
}

export type CurveGeometry = {
    ep1Start: Vector2; ep1End: Vector2;
    ep2Start: Vector2; ep2End: Vector2;
    bezierStart: Vector2; bezierControl: Vector2; bezierEnd: Vector2;
    labelPos: Vector2;
};

export function resolveCurveGeometry(
    refs: CurveAngleRefs,
    landmarkMap: LandmarkMap,
): CurveGeometry | null {
    const ep1A = landmarkMap.get(refs.endplate1[0]);
    const ep1B = landmarkMap.get(refs.endplate1[1]);
    const ep2A = landmarkMap.get(refs.endplate2[0]);
    const ep2B = landmarkMap.get(refs.endplate2[1]);
    if (!ep1A || !ep1B || !ep2A || !ep2B) return null;

    const antDir = anteriorDir(landmarkMap);
    if (!antDir) return null;

    const mid1 = ep1A.clone().add(ep1B).multiplyScalar(0.5);
    const mid2 = ep2A.clone().add(ep2B).multiplyScalar(0.5);

    const bezierStart = mid1;
    const bezierEnd = mid2;

    const midBezier = bezierStart.clone().add(bezierEnd).multiplyScalar(0.5);
    const chord = bezierEnd.clone().sub(bezierStart);
    const chordLen = chord.length();

    const perp1 = new Vector2(-chord.y, chord.x);
    const perp2 = new Vector2(chord.y, -chord.x);

    const bezierPerp = perp1.dot(antDir) > 0 ? perp1 : perp2;
    bezierPerp.normalize();

    const controlOffset = chordLen * 0.4;
    const bezierControl = midBezier.clone().add(bezierPerp.clone().multiplyScalar(controlOffset));
    // The curve peaks at its t=0.5 point, half-way out to the off-curve control
    // point. Anchor the label just beyond that apex so it reads as part of the
    // arc rather than floating away from it.
    const curveApex = midBezier.clone().add(bezierPerp.clone().multiplyScalar(controlOffset * 0.5));
    const labelPos = curveApex.clone().add(bezierPerp.clone().multiplyScalar(controlOffset * 0.4));

    return {
        ep1Start: ep1A, ep1End: ep1B,
        ep2Start: ep2A, ep2End: ep2B,
        bezierStart, bezierControl, bezierEnd,
        labelPos,
    };
}

function isInsideRect(p: Vector2, width: number, height: number): boolean {
    return p.x >= 0 && p.x <= width && p.y >= 0 && p.y <= height;
}

/**
 * Smallest positive `t` at which the ray crosses one of the two boundaries on a
 * single axis (the lower edge at 0 or the upper edge at `max`), or Infinity when
 * the ray is parallel to that axis. `delta`/`origin` are the component of the
 * ray direction and origin for that axis.
 */
function axisExitT(delta: number, origin: number, max: number): number {
    if (delta > 1e-10) {
        const t = (max - origin) / delta;
        return t > 0 ? t : Infinity;
    }
    if (delta < -1e-10) {
        const t = -origin / delta;
        return t > 0 ? t : Infinity;
    }
    return Infinity;
}

/**
 * Clamp a ray endpoint to the image rectangle (0,0)→(width,height).
 * If the endpoint is inside the rect, returns it unchanged.
 * Otherwise, returns the intersection of the ray (origin→endpoint) with the rect boundary.
 * Assumes origin is inside the rect.
 */
export function clampRayToRect(
    origin: Vector2,
    endpoint: Vector2,
    width: number,
    height: number,
): Vector2 {
    const dx = endpoint.x - origin.x;
    const dy = endpoint.y - origin.y;

    // Already inside, or a zero-length ray — nothing to clamp.
    if (isInsideRect(endpoint, width, height)) return endpoint.clone();
    if (Math.abs(dx) < 1e-10 && Math.abs(dy) < 1e-10) return endpoint.clone();

    // Ray: P(t) = origin + t * (endpoint - origin). Take the first boundary
    // crossing in (0,1] — capped at 1 so an endpoint already inside stays put.
    const tMin = Math.min(1, axisExitT(dx, origin.x, width), axisExitT(dy, origin.y, height));

    return new Vector2(origin.x + dx * tMin, origin.y + dy * tMin);
}
